import React, { useEffect, useState } from 'react';
import { FORM_ERROR } from 'final-form';
import styled, { css } from 'styled-components';
import { Auth } from '~/amplify';
import Button from '~/components/atom/Button';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Loading from '~/components/atom/Loading';
import TextButton from '~/components/atom/TextButton';
import { Body, Heading3 } from '~/components/atom/Typography';
import OTPInput from '~/components/molecule/OTPInput';
import InfoBlock from '~/components/molecule/InfoBlock';
import { Form, Field } from '~/components/organism/Forms';
import Modal from '~/components/organism/ModalsV2/Modal';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import Catalog from '~/Catalog';
import { reporter } from '~/hooks/useErrorReporter';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import { validateOtpCode } from '~/util/Validation/OTP';
import { useProfileCognitoPhone } from '../../../../context/ProfileCognitoPhoneContext';
import Icon from '~/components/atom/Icon';
import TOTPSetupInstructions from '~/components/atom/TOTPSetupInstructions';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useUpdateMFAPreferenceAndSyncUser from '~/amplify/utils/updateMFAPreferenceAndSyncUser';
import { buildTotpOtpAuthUrl } from '~/util/buildTotpOtpAuthUrl';
import { isEmpty } from 'ramda';

const text = {
  title: 'Authenticator-app instellen',
  verifyButton: Catalog.mfaTotpVerifyButton,
  codeLengthError: Catalog.mfaTotpCodeLengthError,
  invalidCode: 'Ongeldige verificatiecode. Probeer het opnieuw.',
  noQRCode:
    'Geen QR-code gevonden om authenticator-app in te stellen voor deze gebruiker. Blijft de foutmelding komen? Neem contact met ons op via de chat rechts onderin.',
  cancelButton: 'Annuleren',
  success: {
    body: 'Authenticator-app is succesvol ingesteld voor je account.',
    addBackupMethod: 'Backup toevoegen',
  },
  smsLoginExplanation:
    'Je bent ingelogd met een SMS-code. SMS is alleen een noodoplossing. Stel daarom nu de authenticator-app in. Dat is de aanbevolen methode voor tweestapsverificatie.',
};

type Props = {
  onClose: () => void;

  /** Called after TOTP is enabled when the user still needs to add a verified backup phone. */
  onContinueToPhoneSetup: () => void;

  /** Called after TOTP is enabled to refresh the MFA preferences. */
  onRefreshMFAPreferences: () => Promise<void>;

  /** When true, explains why TOTP setup is required after an SMS-only login. */
  showSmsLoginExplanation?: boolean;
};

const MFASetupTotpModal: React.FC<Props> = ({
  onClose,
  onContinueToPhoneSetup,
  onRefreshMFAPreferences,
  showSmsLoginExplanation = false,
}) => {
  const { phoneNumber, phoneVerified } = useProfileCognitoPhone();
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { id: accountId } = useCurrentAccount();
  const { updateMFAPreferenceWithSync } = useUpdateMFAPreferenceAndSyncUser();

  const addToast = useAddToast();

  useEffect(() => {
    const setup = async () => {
      try {
        const currentUser = await Auth.getCurrentUser();
        const result = await Auth.setUpTOTP();
        const cognitoId = currentUser.username;

        setQrCode(
          buildTotpOtpAuthUrl({
            accountIdentifier: cognitoId,
            sharedSecret: result.sharedSecret,
          }),
        );
      } catch (err) {
        reporter.captureMessage(
          `MFASetupTotpModal: Failed to setUpTOTP: ${err}`,
          'error',
        );
        setError(Catalog.genericUnknownErrorMessage);
      } finally {
        setLoading(false);
      }
    };

    void setup();
  }, []);

  const onVerifyTotp = async ({ code }: { code: string }) => {
    try {
      await Auth.verifyTOTPSetup({ code });

      await updateMFAPreferenceWithSync({
        accountId,
        update: { totp: 'ENABLED' },
      });

      await onRefreshMFAPreferences();

      const hasVerifiedPhone = !!phoneNumber && phoneVerified;

      if (hasVerifiedPhone) {
        addToast([formatToastMessage(text.success.body, 'success')]);
        onClose();
      } else {
        onContinueToPhoneSetup();
      }
      return;
    } catch (err) {
      reporter.captureMessage(
        `MFASetupTotpModal: Failed to verifyTOTPSetup: ${err}`,
        'error',
      );
      setError(text.invalidCode);
      return { [FORM_ERROR]: text.invalidCode };
    }
  };

  const validate = ({ code }: { code: string }) => {
    if (!validateOtpCode({ code })) {
      return { code: text.codeLengthError };
    }
    return {};
  };

  return (
    <Overlay onClose={onClose}>
      <Modal onClose={onClose} maxWidth="520px">
        <JustificationContainer
          direction="column"
          gap="l"
          padding={['xl', 'xl', 'l', 'xl']}
          width="100%"
        >
          <JustificationContainer gap="s" width="100%" align="center">
            <Icon name="key" strokeWidth={2.3} />
            <Heading3 size="m" margin={[null]}>
              {text.title}
            </Heading3>
          </JustificationContainer>

          {showSmsLoginExplanation && (
            <InfoBlock
              appearance="info"
              padding={['base']}
              inline
              icon={{
                name: 'info',
                backgroundColor: { group: 'info' },
                color: { group: 'white' },
              }}
              headerText={null}
            >
              <Body size="base" margin={[null]}>
                {text.smsLoginExplanation}
              </Body>
            </InfoBlock>
          )}

          {(() => {
            if (loading) {
              return (
                <JustificationContainer
                  align="center"
                  justification="center"
                  padding={['l', null]}
                  width="100%"
                >
                  <Loading />
                </JustificationContainer>
              );
            }

            if (error && !qrCode) {
              return (
                <Body size="base" margin={[null]} color={{ group: 'danger' }}>
                  {error}
                </Body>
              );
            }

            if (!qrCode) {
              return (
                <JustificationContainer direction="column" gap="l" width="100%">
                  <Body size="base" margin={[null]} color={{ group: 'danger' }}>
                    {text.noQRCode}
                  </Body>
                </JustificationContainer>
              );
            }

            return null;
          })()}

          {!loading && qrCode ? (
            <>
              <Form
                initialValues={{ code: '' }}
                onSubmit={onVerifyTotp}
                validate={validate}
              >
                {({
                  handleSubmit,
                  submitting,
                  submitError: formError,
                  errors,
                }) => (
                  <StyledForm onSubmit={handleSubmit}>
                    <JustificationContainer
                      direction="column"
                      gap="m"
                      width="100%"
                    >
                      <TOTPSetupInstructions qrCode={qrCode} />

                      <Field name="code">
                        {({ input }) => (
                          <OTPInput
                            length={6}
                            value={input.value ?? ''}
                            onChange={input.onChange}
                            onBlur={input.onBlur}
                            disabled={submitting}
                            autoFocus
                          />
                        )}
                      </Field>

                      {(formError || error) && (
                        <Body
                          margin={[null]}
                          color={{ group: 'danger' }}
                          size="base"
                        >
                          {formError || error}
                        </Body>
                      )}
                    </JustificationContainer>

                    <FormFooter justification="end" gap="base" align="center">
                      <TextButton
                        label={text.cancelButton}
                        onClick={onClose}
                        appearance="primary"
                        size="medium"
                      />
                      <Button
                        appearance="primary"
                        size="medium"
                        type="submit"
                        disabled={submitting || !isEmpty(errors)}
                        loading={submitting}
                        label={text.verifyButton}
                      />
                    </FormFooter>
                  </StyledForm>
                )}
              </Form>
            </>
          ) : null}
        </JustificationContainer>
      </Modal>
    </Overlay>
  );
};

const StyledForm = styled.form`
  width: 100%;
`;

const FormFooter = styled(JustificationContainer)(
  ({ theme }) => css`
    margin-top: ${theme.space('m')};
    padding-top: ${theme.space('m')};
  `,
);

export default MFASetupTotpModal;
